import { useQueryString } from "hooks/general-hooks";
import { SPECS_TO_HERO_TREES } from "lib/classes-and-specs";
import { updateQueryParamsIfChanged } from "lib/general-helper";
import { getHeroTalentQueryParamFormat } from "lib/wowHelpers";
import React, { useEffect, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router";

// `defaultHero` is the data-driven side ("left"/"right") to select when the URL doesn't specify a
// hero. Pass null while the page data is still loading - the selection stays a provisional "left"
// and is not written to the URL until the default resolves or the user picks a side.
export function useHeroQueryParams(displayClassName, displaySpecName, defaultHero = null) {
  const params = useQueryString();
  const history = useHistory();
  const location = useLocation();
  const heroArray = SPECS_TO_HERO_TREES[displayClassName].specs[displaySpecName];
  const leftTreeName = getHeroTalentQueryParamFormat(heroArray[0]);
  const rightTreeName = getHeroTalentQueryParamFormat(heroArray[1]);
  const paramHero = params["hero"] === leftTreeName ? "left" : params["hero"] === rightTreeName ? "right" : null;

  const [selectedHero, setSelectedHeroState] = useState(paramHero ?? defaultHero ?? "left");
  // Whether the selection is authoritative (from the URL, the data-driven default, or a user
  // click) rather than the provisional fallback.
  const heroResolved = useRef(paramHero !== null || defaultHero !== null);

  const setSelectedHero = (side) => {
    heroResolved.current = true;
    setSelectedHeroState(side);
  };

  // Apply the data-driven default once it resolves, unless the URL already specified a hero.
  useEffect(() => {
    if (!heroResolved.current && defaultHero !== null) {
      heroResolved.current = true;
      setSelectedHeroState(defaultHero);
      params["hero"] = getHeroTalentQueryParamFormat(heroArray[defaultHero === "left" ? 0 : 1]);
      updateQueryParamsIfChanged(history, location, params);
    }
  }, [defaultHero]);

  // This use effect sets the selection of the HeroTalentSelector.
  useEffect(() => {
    if (!heroResolved.current) {
      return;
    }
    const selectedHeroTree = selectedHero == "left" ? 0 : 1;
    params["hero"] = getHeroTalentQueryParamFormat(heroArray[selectedHeroTree]);
    updateQueryParamsIfChanged(history, location, params);
  }, [selectedHero]);

  // This useEffect handles when the user navigates back - reloads the page when it's a diff selected hero
  useEffect(() => {
    const paramHeroTree = params["hero"];
    if (paramHeroTree === leftTreeName || paramHeroTree === rightTreeName) {
      heroResolved.current = true;
    }
    if (!heroResolved.current) {
      return;
    }

    if (paramHeroTree === leftTreeName && selectedHero === "right") {
      setSelectedHeroState("left");
    } else if (paramHeroTree === rightTreeName && selectedHero === "left") {
      setSelectedHeroState("right");
    }
    const selectedHeroTree = selectedHero == "left" ? 0 : 1;
    params["hero"] = getHeroTalentQueryParamFormat(heroArray[selectedHeroTree]);
    updateQueryParamsIfChanged(history, location, params);
  }, [params]);

  // While unresolved, surface the incoming default immediately so the first data-bearing render
  // (including SSR) already shows the right tree instead of flashing "left" for a frame.
  return { selectedHero: heroResolved.current ? selectedHero : defaultHero ?? selectedHero, setSelectedHero };
}
